import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import type { ChartOptions } from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface SectionPerformanceChartProps {
  sectionRatings: Record<string, number>;
  className?: string;
}

const SECTION_DISPLAY_NAMES: Record<string, string> = {
  'strengths': 'Pontos Fortes',
  'areas_for_growth': 'Áreas de Crescimento',
  'communication_style': 'Estilo de Comunicação',
  'development_plan': 'Plano de Desenvolvimento',
  'detailed_analysis': 'Análise Detalhada',
};

export const SectionPerformanceChart: React.FC<SectionPerformanceChartProps> = ({ 
  sectionRatings, 
  className = '' 
}) => {
  // Preparar dados para o gráfico
  const sections = Object.entries(sectionRatings)
    .map(([key, rating]) => ({
      key,
      name: SECTION_DISPLAY_NAMES[key] || key.replace('_', ' '),
      rating: Number(rating.toFixed(1))
    }))
    .sort((a, b) => b.rating - a.rating); // Ordenar por rating (melhor primeiro)

  const getBarColor = (rating: number): string => {
    if (rating >= 4.5) return '#10B981'; // Verde
    if (rating >= 4.0) return '#34D399'; // Verde claro
    if (rating >= 3.5) return '#FBBF24'; // Amarelo
    if (rating >= 3.0) return '#F59E0B'; // Laranja
    return '#EF4444'; // Vermelho
  };

  const data = {
    labels: sections.map(s => s.name),
    datasets: [
      {
        label: 'Avaliação Média',
        data: sections.map(s => s.rating),
        backgroundColor: sections.map(s => getBarColor(s.rating)),
        borderColor: sections.map(s => getBarColor(s.rating)),
        borderWidth: 1,
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Performance por Seção do Relatório',
        font: {
          size: 16,
          weight: 'bold',
        },
        color: '#1F2937',
        padding: {
          bottom: 20,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        titleColor: '#F9FAFB',
        bodyColor: '#F9FAFB',
        borderColor: '#374151',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context) {
            const rating = context.parsed.y;
            let performance = '';
            if (rating >= 4.5) performance = 'Excelente';
            else if (rating >= 4.0) performance = 'Muito Bom';
            else if (rating >= 3.5) performance = 'Bom';
            else if (rating >= 3.0) performance = 'Regular';
            else performance = 'Precisa Melhorar';
            
            return `${rating}/5.0 (${performance})`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
          },
          color: '#6B7280',
          maxRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        max: 5,
        grid: {
          color: '#F3F4F6',
        },
        ticks: {
          font: {
            size: 12,
          },
          color: '#6B7280',
          stepSize: 0.5,
        },
      },
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart',
    },
  };

  if (sections.length === 0) {
    return (
      <div className={`flex items-center justify-center h-64 bg-gray-50 rounded-lg ${className}`}>
        <div className="text-center">
          <div className="text-gray-400 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-gray-500">Nenhum dado de seção disponível</p>
          <p className="text-sm text-gray-400 mt-1">Os usuários ainda não avaliaram seções específicas</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
      <div className="h-80">
        <Bar data={data} options={options} />
      </div>
      
      {/* Legenda de performance */}
      <div className="mt-4 flex flex-wrap gap-4 justify-center text-sm">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-500 rounded mr-2"></div>
          <span className="text-gray-600">Excelente (4.5+)</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-400 rounded mr-2"></div>
          <span className="text-gray-600">Muito Bom (4.0+)</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-yellow-400 rounded mr-2"></div>
          <span className="text-gray-600">Bom (3.5+)</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-orange-400 rounded mr-2"></div>
          <span className="text-gray-600">Regular (3.0+)</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-500 rounded mr-2"></div>
          <span className="text-gray-600">Precisa Melhorar (&lt;3.0)</span>
        </div>
      </div>
    </div>
  );
};